'use client'

import { Cell, Divider, Text } from '@vinted/web-ui'

import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { SortByOption, Filter } from '@marketplace-web/domain/catalog-data'

import { clickFilterEvent } from '_libs/common/event-tracker/events'
import { Screen } from 'constants/tracking/screens'

import { useCatalogContext } from '../../../../CatalogProvider'
import SelectableItemList, { SelectableListItem } from '../../common/SelectableItemList'
import useFetchDynamicFilters from '../../../../hooks/useFetchDynamicFilters'
import FilterDropdown from '../../common/FilterDropdown'

type Props = {
  learnMoreUrl: string | null | undefined
  useDropdownBottomEndPlacement?: boolean
}

type SortItem = SelectableListItem<SortByOption, SortByOption>

const SortFilter = ({ useDropdownBottomEndPlacement = false, learnMoreUrl }: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('catalog.filters.sort')
  const fetchDynamicFilters = useFetchDynamicFilters()
  const {
    state: { filters, dynamicFilters, searchSession },
  } = useCatalogContext()
  const { globalSearchSessionId, globalCatalogBrowseSessionId } = searchSession
  const searchSessionId = searchSession.sessionId
  const { selectedDynamicFilters } = dynamicFilters

  const selected = filters.sortBy || SortByOption.Relevance

  const handleOnFilterClick = () => {
    track(
      clickFilterEvent({
        filter: Filter.Sort,
        screen: Screen.CatalogFilters,
        searchSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
      }),
    )
  }

  const handleItemClick = (item: SortItem) => {
    fetchDynamicFilters({
      selectedDynamicFilters,
      selectedFilters: { sortBy: item.value },
    })
  }

  const renderLearnMoreMessage = () => {
    if (!learnMoreUrl) return null

    const action = (
      <a key="action" href={learnMoreUrl}>
        {translate('learn_more.action')}
      </a>
    )

    return (
      <>
        <Divider />
        <Cell>
          <Text
            type={Text.Type.Caption}
            text={translate('learn_more.message', { action })}
            as="span"
          />
        </Cell>
      </>
    )
  }

  const renderDropdown = () => {
    const items = Object.values(SortByOption).map(option => ({
      id: option,
      title: translate(`options.${option}`),
      value: option,
    }))

    return (
      <div className="u-ui-padding-vertical-x-small" data-testid="sort-by-dropdown">
        <SelectableItemList<SortByOption, SortByOption>
          name="sort_by"
          isMultiSelect={false}
          items={items}
          selected={[selected || SortByOption.Relevance]}
          onItemClick={handleItemClick}
          itemButtonTestId="sort-by-list"
          disableButtonClicks
        />
        {renderLearnMoreMessage()}
      </div>
    )
  }

  return (
    <div className="u-ui-margin-right-regular u-ui-margin-bottom-regular">
      <FilterDropdown
        title={translate('name')}
        placement={useDropdownBottomEndPlacement ? 'bottom-end' : undefined}
        areAnyOfItemsSelected={!!selected && selected !== SortByOption.Relevance}
        testId="catalog--sort-filter"
        onFilterClick={handleOnFilterClick}
        closeOnClick
        useChipAsTrigger
      >
        {renderDropdown()}
      </FilterDropdown>
    </div>
  )
}

export default SortFilter
